import { defineStore } from 'pinia';
import { reactive } from 'vue';
import type { OrderTemplateSummaryEntity } from '@/orderTemplateModule/domains/entities/orderTemplate';
import { diContainer } from '@/coreContainer/container';

/**
 * 出力書式の選択肢
 */
type ExportFormat = 'default' | 'custom' | 'file';

/**
 * 発注書出力モーダルの状態を管理するストア
 */
export const useExportOrderModalStore = defineStore('exportOrderModal', () => {
  const state = reactive<{
    selectedFormat: ExportFormat;
    selectedTemplateId: string;
    selectedFile: File | null;
    templates: OrderTemplateSummaryEntity[];
    loading: boolean;
    errorMessage: string;
  }>({
    selectedFormat: 'default',
    selectedTemplateId: '',
    selectedFile: null,
    templates: [],
    loading: false,
    errorMessage: '',
  });

  /**
   * モーダル状態を初期化する
   */
  const $reset = () => {
    state.selectedFormat = 'default';
    state.selectedTemplateId = '';
    state.selectedFile = null;
    state.templates = [];
    state.loading = false;
    state.errorMessage = '';
  };

  /**
   * 出力書式の種別を設定する
   * @param format - 選択した書式種別
   */
  const setSelectedFormat = (format: ExportFormat) => {
    state.selectedFormat = format;
    if (format === 'default') {
      state.selectedTemplateId = '';
      state.selectedFile = null;
      return;
    }
    if (format === 'file') {
      state.selectedTemplateId = '';
      return;
    }
    state.selectedFile = null;
    if (!state.selectedTemplateId && state.templates.length > 0) {
      state.selectedTemplateId = state.templates[0].templateId;
    }
  };

  /**
   * 選択中のテンプレートIDを更新する
   * @param templateId - 選択したテンプレートID
   */
  const setSelectedTemplateId = (templateId: string) => {
    state.selectedTemplateId = templateId;
  };

  /**
   * 選択中のファイルを更新する
   * @param file - 選択したファイル
   */
  const setSelectedFile = (file: File | null) => {
    state.selectedFile = file;
  };

  /**
   * テンプレート一覧を取得する
   */
  const fetchTemplates = async (): Promise<void> => {
    state.loading = true;
    state.errorMessage = '';
    const result = await diContainer.getOrderTemplateListUseCase.Execute();
    if (result.status.type === 'success') {
      state.templates = result.status.data;
      if (state.selectedFormat === 'custom') {
        if (state.templates.length === 0) {
          state.selectedFormat = 'default';
          state.selectedTemplateId = '';
        } else if (!state.selectedTemplateId) {
          state.selectedTemplateId = state.templates[0].templateId;
        }
      }
    } else {
      state.errorMessage = result.status.error.message ?? 'テンプレートの取得に失敗しました';
    }
    state.loading = false;
  };

  return {
    state,
    $reset,
    setSelectedFormat,
    setSelectedTemplateId,
    setSelectedFile,
    fetchTemplates,
  };
});
